



































































import {
  computed, defineComponent, getCurrentInstance, onMounted, ref, watch,
} from 'vue';
import {
  SourceType, type Pixel, type Point, type Rect,
} from '@/data';
import { labelWatcherProps, useLabelWatcher } from '@/vuejs/composables/useLabelWatcher';
import type { UseLabelWatcherProps } from '@/vuejs/composables/useLabelWatcher';
import SelectionArrows from '@/vuejs/components/canvas/SelectionArrows.vue';

interface LabelSvgProps extends UseLabelWatcherProps {
  isFocused: boolean;
  currentViewX: Pixel;
}

const MAX_BAR_TAG_WIDTH = 150;
const TEXT_MARGIN = 4;
const ELLIPSIS_WIDTH = 15;
const LABEL_TEXT_HEIGHT = 25;
const MOMENT_BAR_CIRCLE_DIAMETER = 10;
const TEXT_RIGHT_MARGIN = 16;

export default defineComponent({
  name: 'LabelSvg',
  components: {
    SelectionArrows,
  },
  props: {
    ...labelWatcherProps,
    isFocused: { type: Boolean },
    currentViewX: { type: Number, default: 0 },
  },
  setup(props) {
    const {
      rects, isMomentBar, classes,
    } = useLabelWatcher(props as LabelSvgProps);

    /**
     * A reference to the label <rect> element
     */
    const svgRect = ref<SVGRectElement | null>(null);

    /**
     * A reference to the <text> element
     */
    const text = ref<SVGTextContentElement | null>(null);

    /**
     * Updates the text value through the DOM
     * This is necessary as SVG does not support CSS text-overflow
     * We have to read the rastered text width through the DOM
     */
    const updateText = () => {
      if (!text.value || !props.label) return;

      const tag = props.label.symbol || props.label.tag || '';

      // Calculate the max text width
      // If the label is a bar, use a static constant
      const width = props.label.duration
        ? rects.value[0].width - TEXT_MARGIN * 2
        : MAX_BAR_TAG_WIDTH;

      text.value.textContent = tag;

      // If the text fits, finish the function
      if (text.value.getComputedTextLength() < width) return;

      const availableWidth = width - ELLIPSIS_WIDTH;

      // If the text does not fit, iterate the length until it does
      for (let i = tag.length - 1; i >= 0; i -= 1) {
        const textWidth = text.value.getSubStringLength(0, i);
        // Remove 15 pixels from the width to fit the ellipsis
        if (textWidth < availableWidth) {
          text.value.textContent = `${tag.slice(0, i)}...`;
          return;
        }
      }
      // If nothing fits, remove the text
      text.value.textContent = '';
    };

    /**
     * The current position of the text element
     */
    const textPos = ref<Point>({ x: 0, y: 0 } as Point);

    const textX = computed(() => {
      if (!text.value || !rects.value.length) return 0;
      const computedTextLength = text.value.getComputedTextLength();
      const textEnd = props.currentViewX + computedTextLength;
      const rectEnd = rects.value[0].x + rects.value[0].width;
      let origin = rects.value[0].x as number;
      if (textEnd + TEXT_RIGHT_MARGIN >= rectEnd && textEnd > 0) {
        origin = rectEnd - computedTextLength - TEXT_RIGHT_MARGIN;
      } else if (rects.value[0].x < props.currentViewX) {
        origin = props.currentViewX;
      }
      return origin + TEXT_MARGIN;
    });

    const updateTextPos = () => {
      if (props.label?.duration) {
        textPos.value.x = textX.value as Pixel;
        textPos.value.y = (rects.value[0].y + LABEL_TEXT_HEIGHT) as Pixel;
      } else {
        textPos.value.x = (rects.value[0].x + rects.value[0].width / 2) as Pixel;
        textPos.value.y = (rects.value[0].y + rects.value[0].height - 2) as Pixel;
      }
    };

    /**
     * The actual displayed rectangle
     * Can be smaller than rect to leave room for the moment handle
     * Can be smaller than rect to leave room for the bar text
     */
    const displayRects = ref<Rect[]>([{
      x: 0, y: 0, width: 0, height: 0,
    }] as Rect[]);

    const cloneRects = (r: Rect[]) => r.map((rect) => ({ ...rect }));

    const updateDisplayRect = () => {
      displayRects.value = cloneRects(rects.value);
      if (!props.label?.duration) {
        let vMargin = LABEL_TEXT_HEIGHT;
        if (isMomentBar.value) {
          displayRects.value[0].y = (rects.value[0].y + MOMENT_BAR_CIRCLE_DIAMETER) as Pixel;
          vMargin += MOMENT_BAR_CIRCLE_DIAMETER;
        }
        displayRects.value[0].height = (rects.value[0].height - vMargin) as Pixel;
      }
    };

    const onSelectionChange = () => {
      if (props.isSelected && props.isFocused) svgRect.value?.focus({ preventScroll: true });
    };

    onMounted(onSelectionChange);

    watch(() => props.currentViewX, updateTextPos);
    watch(
      () => rects.value,
      () => {
        updateDisplayRect();
        updateText();
        updateTextPos();
      },
    );

    /**
     * When selection changes in the focused canvas, focus the selected label
     */
    watch(() => props.isSelected, onSelectionChange);

    const store = getCurrentInstance()?.proxy.$store;

    const isLabelDisabled = computed(
      () => props.label?.repeat !== undefined
            && props.label?.repeat !== store?.state.synchro.currentRepeat,
    );

    const isGridBackground = computed(
      () => props.sourceType === SourceType.Grid && props.label?.type === 'Pattern',
    );

    const isGridHarmony = computed(
      () => props.sourceType === SourceType.Grid && props.label?.type === 'Harmony',
    );

    const isGridStructure = computed(
      () => props.sourceType === SourceType.Grid && props.label?.type === 'Structure',
    );

    const isGridGesture = computed(
      () => props.sourceType === SourceType.Grid && props.label?.type === 'Gesture',
    );
    return {
      rects,
      isMomentBar,
      classes,
      displayRects,
      textPos,
      text,
      isLabelDisabled,
      isGridBackground,
      isGridHarmony,
      isGridStructure,
      isGridGesture,
    };
  },
});
